// Запросы раздела «Люди»: ключи кэша, хуки чтения и изменения.
//
// Правило 11 (поведение во времени задано числами) реализуется здесь, а не в каждом экране:
//   * placeholderData: keepPreviousData — при смене отбора строки остаются на месте, а не
//     сменяются пустотой на время запроса;
//   * скелет рисуется только там, где данных нет ДАЖЕ в кэше прошлой сессии — это решает
//     ScreenState по data === undefined;
//   * спиннера поверх уже показанного числа нет нигде.
//
// Общий корень ключей 'people' даёт одну команду на сброс раздела: сменили статус — устарели
// и реестр, и счётчики представлений, и карточка, и история статусов.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useLocation } from 'react-router';

import type { DebtDecisionKind, DebtIn, FinalSettlementOut } from '@/shared/api/endpoints/losses';
import {
  createDebt,
  decideDebt,
  fetchFinalSettlement,
  fetchUniformIssues,
  fetchTicketState,
  fetchUniformItems,
  issueUniform,
  registerEarlyLeave,
  writeOffDebt,
} from '@/shared/api/endpoints/losses';
import type { ListResponse } from '@/shared/api/types';
import type {
  AssignmentIn,
  BulkAssignmentIn,
  BulkAssignmentOut,
  AssignmentOut,
  BulkResult,
  BulkStatusIn,
  DocumentIn,
  DocumentOut,
  DuplicateReport,
  DuplicatesQuery,
  EmployeeIn,
  EmployeeOut,
  EmployeePatch,
  EmployeesQuery,
  EmploymentView,
  ExtendIn,
  FireIn,
  RateChangeIn,
  RateChangeOut,
  StatusChangeIn,
  StatusHistoryOut,
  TransferIn,
} from '@/shared/api/endpoints/people';
import {
  addDocument,
  bulkStatus,
  changeEmployeeStatus,
  changeRate,
  closeAssignment,
  createAssignment,
  createAssignmentsBulk,
  createEmployee,
  checkDuplicates,
  extendAssignment,
  fireEmployee,
  getEmployee,
  listDocuments,
  listEmployeeAssignments,
  listEmployees,
  listEmploymentViews,
  listRateChanges,
  listStatusHistory,
  patchEmployee,
  transferAssignment,
} from '@/shared/api/endpoints/people';
import type { ApiError } from '@/shared/errors/api-error';
import type { ApplicationIn, ApplicationOut, FactOut } from '@/shared/api/endpoints/payouts';
import { createApplication, fetchApplications, fetchFacts } from '@/shared/api/endpoints/payouts';
import type { PayslipOut } from '@/shared/api/endpoints/payslip';
import { fetchPayslip } from '@/shared/api/endpoints/payslip';
import { вСозданнуюВедомость } from '@/shared/people/последний-день';
import { parsePath } from '@/shared/routing/paths';
import { подтверждение } from '@/shared/ui/toast/подтверждение';

import { bulkChangeToast, stateChangeToast } from './registry-tabs';
import { SECTION, STATUS_ALL } from './route';
import type { RegistryTab } from './route';

export { newIdempotencyKey } from '@/shared/api/idempotency';

export const peopleKeys = {
  all: ['people'] as const,
  employees: (params: EmployeesQuery) => ['people', 'employees', params] as const,
  employee: (employeeId: string) => ['people', 'employee', employeeId] as const,
  views: () => ['people', 'views'] as const,
  documents: (employeeId: string) => ['people', 'documents', employeeId] as const,
  statusHistory: (employeeId: string) => ['people', 'status-history', employeeId] as const,
  assignments: (employeeId: string) => ['people', 'assignments', employeeId] as const,
  rateChanges: (assignmentId: string) => ['people', 'rate-changes', assignmentId] as const,
  duplicates: (params: DuplicatesQuery) => ['people', 'duplicates', params] as const,
  payouts: (employeeId: string) => ['people', 'payouts', employeeId] as const,
  payoutFacts: (employeeId: string) => ['people', 'payout-facts', employeeId] as const,
  payslip: (employeeId: string, year: number, month: number) =>
    ['people', 'payslip', employeeId, year, month] as const,
  /**
   * Предупреждение о дубле от заведения карточки. Приезжает ВМЕСТЕ с созданной карточкой и
   * в GET карточки его уже нет: сервер отдаёт его один раз. Кладём в кэш под своим ключом,
   * чтобы карточка, открывшаяся сразу после мастера, показала то же предупреждение.
   */
  duplicateWarning: (employeeId: string) => ['people', 'duplicate-warning', employeeId] as const,
};

// ── Чтение ────────────────────────────────────────────────────────────────────────────────

export function useEmployeesQuery(
  params: EmployeesQuery,
): UseQueryResult<ListResponse<EmployeeOut>, ApiError> {
  return useQuery<ListResponse<EmployeeOut>, ApiError>({
    queryKey: peopleKeys.employees(params),
    queryFn: ({ signal }) => listEmployees(params, signal),
    placeholderData: keepPreviousData,
  });
}

export function useEmploymentViewsQuery(): UseQueryResult<ListResponse<EmploymentView>, ApiError> {
  return useQuery<ListResponse<EmploymentView>, ApiError>({
    queryKey: peopleKeys.views(),
    queryFn: ({ signal }) => listEmploymentViews(signal),
    placeholderData: keepPreviousData,
  });
}

export function useEmployeeQuery(employeeId: string): UseQueryResult<EmployeeOut, ApiError> {
  return useQuery<EmployeeOut, ApiError>({
    queryKey: peopleKeys.employee(employeeId),
    queryFn: ({ signal }) => getEmployee(employeeId, signal),
  });
}

export function useDocumentsQuery(
  employeeId: string,
): UseQueryResult<ListResponse<DocumentOut>, ApiError> {
  return useQuery<ListResponse<DocumentOut>, ApiError>({
    queryKey: peopleKeys.documents(employeeId),
    queryFn: ({ signal }) => listDocuments(employeeId, signal),
  });
}

export function useStatusHistoryQuery(
  employeeId: string,
): UseQueryResult<ListResponse<StatusHistoryOut>, ApiError> {
  return useQuery<ListResponse<StatusHistoryOut>, ApiError>({
    queryKey: peopleKeys.statusHistory(employeeId),
    queryFn: ({ signal }) => listStatusHistory(employeeId, signal),
  });
}

export function useAssignmentsQuery(
  employeeId: string,
): UseQueryResult<ListResponse<AssignmentOut>, ApiError> {
  return useQuery<ListResponse<AssignmentOut>, ApiError>({
    queryKey: peopleKeys.assignments(employeeId),
    queryFn: ({ signal }) => listEmployeeAssignments(employeeId, signal),
  });
}

export function useRateChangesQuery(
  assignmentId: string,
  enabled = true,
): UseQueryResult<ListResponse<RateChangeOut>, ApiError> {
  return useQuery<ListResponse<RateChangeOut>, ApiError>({
    queryKey: peopleKeys.rateChanges(assignmentId),
    queryFn: ({ signal }) => listRateChanges(assignmentId, signal),
    enabled,
  });
}

/**
 * Живая проверка дубля в мастере. Запускается по расфокусу поля, а не на каждой букве:
 * телефон набирается одиннадцатью нажатиями, и одиннадцать запросов подряд дали бы одиннадцать
 * мигающих предупреждений, из которых первые десять — про недонабранный номер.
 */
export function useDuplicatesQuery(
  params: DuplicatesQuery,
  enabled: boolean,
): UseQueryResult<DuplicateReport, ApiError> {
  return useQuery<DuplicateReport, ApiError>({
    queryKey: peopleKeys.duplicates(params),
    queryFn: ({ signal }) => checkDuplicates(params, signal),
    enabled,
  });
}

/** Предупреждение о дубле, оставшееся от заведения карточки. */
export function useDuplicateWarning(employeeId: string): DuplicateReport | undefined {
  const client = useQueryClient();
  return client.getQueryData<DuplicateReport>(peopleKeys.duplicateWarning(employeeId));
}

// ── Изменения ─────────────────────────────────────────────────────────────────────────────

export function useCreateEmployeeMutation(
  idempotencyKey: string,
): UseMutationResult<EmployeeOut, ApiError, EmployeeIn> {
  const client = useQueryClient();
  return useMutation<EmployeeOut, ApiError, EmployeeIn>({
    mutationFn: (body) => createEmployee(body, idempotencyKey),
    // Куда человек попал и что с этим делать: реестр открывается на «На вахте», а новый
    // сотрудник заводится в резерве (контракт: `EmployeeIn.status` по умолчанию `reserve`), и
    // без этой строки он «пропал» бы из списка, который человек видит. Вкладка «Резерв»
    // открывается сама (`NewEmployeePanel`), а подсказка говорит, как перевести на вахту.
    //
    // Без рода: подлежащее «человек», а не «заведён» про неизвестно кого. Тосты видят и о
    // женщинах, и «Заведён» про Иванову читается как ошибка.
    meta: подтверждение(
      'Человек заведён в состоянии «Резерв»',
      'Перевести на вахту можно кнопкой «Сменить статус»',
    ),
    onSuccess: (employee) => {
      // Совпадения приезжают один раз, вместе с созданной карточкой. Сохраняем их под своим
      // ключом: карточка откроется следом и покажет то же предупреждение.
      if (employee.duplicate_warning != null && employee.duplicate_warning.found) {
        client.setQueryData(peopleKeys.duplicateWarning(employee.id), employee.duplicate_warning);
      }
      void client.invalidateQueries({ queryKey: peopleKeys.all });
    },
  });
}

export function usePatchEmployeeMutation(
  employeeId: string,
): UseMutationResult<EmployeeOut, ApiError, EmployeePatch> {
  const client = useQueryClient();
  return useMutation<EmployeeOut, ApiError, EmployeePatch>({
    mutationFn: (body) => patchEmployee(employeeId, body),
    meta: подтверждение('Карточка сохранена'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: peopleKeys.all });
    },
  });
}

/**
 * Смена состояния одного человека.
 *
 * `открытаяВкладка` — вкладка реестра, на которой человек нажал «Сменить статус». По ней
 * подтверждение называет, куда ушла строка («Теперь: вкладка «Резерв»»): без этого человек
 * из открытой вкладки просто исчезает, и это читается как потеря. Не передали — «куда» не
 * говорится, как на вкладке «Все».
 */
export function useStatusMutation(
  employeeId: string,
  открытаяВкладка: RegistryTab = STATUS_ALL,
): UseMutationResult<EmployeeOut, ApiError, StatusChangeIn> {
  const client = useQueryClient();
  return useMutation<EmployeeOut, ApiError, StatusChangeIn>({
    mutationFn: (body) => changeEmployeeStatus(employeeId, body),
    // Состояние берётся из ОТВЕТА сервера, а не из того, что просили: что записал сервер, то и
    // показываем. «Состояние», а не «статус», как и на вкладках над списком.
    meta: подтверждение<StatusChangeIn, EmployeeOut>(
      (_ввод, человек) => stateChangeToast(открытаяВкладка, человек.status).title,
      (_ввод, человек) => stateChangeToast(открытаяВкладка, человек.status).note,
    ),
    onSuccess: () => {
      // Статус меняет и строку реестра, и счётчики представлений, и историю статусов.
      void client.invalidateQueries({ queryKey: peopleKeys.all });
    },
  });
}

export function useFireMutation(
  employeeId: string,
): UseMutationResult<EmployeeOut, ApiError, FireIn> {
  const client = useQueryClient();
  return useMutation<EmployeeOut, ApiError, FireIn>({
    mutationFn: (body) => fireEmployee(employeeId, body),
    // Уволенного нет во вкладке «На вахте», где человек его только что искал: без подсказки,
    // куда он делся, строка просто исчезает из списка. Подлежащее «человек» и «увольнение», а не
    // «уволен» и «он»: рода у тоста нет.
    meta: подтверждение('Увольнение оформлено', 'Теперь человек во вкладке «Уволены»'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: peopleKeys.all });
    },
  });
}

export function useAddDocumentMutation(
  employeeId: string,
  idempotencyKey: string,
): UseMutationResult<DocumentOut, ApiError, DocumentIn> {
  const client = useQueryClient();
  return useMutation<DocumentOut, ApiError, DocumentIn>({
    mutationFn: (body) => addDocument(employeeId, body, idempotencyKey),
    meta: подтверждение('Документ добавлен'),
    onSuccess: () => {
      // Удостоверение охранника несёт разряд, а разряд — отбор в реестре: сбрасываем весь раздел.
      void client.invalidateQueries({ queryKey: peopleKeys.all });
    },
  });
}

/**
 * Смена состояния пачкой. `открытаяВкладка` — вкладка, ИЗ КОТОРОЙ выделяли людей: все
 * изменённые ушли в одно состояние, и подтверждение называет эту вкладку.
 *
 * Подтверждение здесь единственное, что человек увидит: панель выделения на успехе исчезает
 * вместе с выделением, а слова про не тронутых («вне вашей области видимости») жили в ней.
 * Поэтому не тронутых называет и оно.
 */
export function useBulkStatusMutation(
  открытаяВкладка: RegistryTab = STATUS_ALL,
): UseMutationResult<BulkResult, ApiError, BulkStatusIn> {
  const client = useQueryClient();
  return useMutation<BulkResult, ApiError, BulkStatusIn>({
    mutationFn: (body) => bulkStatus(body),
    meta: подтверждение<BulkStatusIn, BulkResult>(
      (ввод, итог) => bulkChangeToast(открытаяВкладка, ввод.status, итог)?.title ?? null,
      (ввод, итог) => bulkChangeToast(открытаяВкладка, ввод.status, итог)?.note,
    ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: peopleKeys.all });
    },
  });
}

export function useCreateAssignmentMutation(
  idempotencyKey: string,
): UseMutationResult<AssignmentOut, ApiError, AssignmentIn> {
  const client = useQueryClient();
  return useMutation<AssignmentOut, ApiError, AssignmentIn>({
    mutationFn: (body) => createAssignment(body, idempotencyKey),
    // Подтверждения НЕТ намеренно: панель постановки НЕ закрывается после действия и сама
    // говорит подробнее — «Багровский поставлен на „СБК-2 · КПП“ с 05.09.2026», а под этим
    // предупреждения и укомплектованность объекта. Всплывающее «Человек поставлен на объект»
    // рядом — тот же факт вдвое короче и вторыми словами. Поймано сквозной проверкой бюджета
    // нажатий: она нашла на экране ДВЕ фразы «поставлен на» вместо одной.
    onSuccess: () => {
      // Постановка меняет и назначения человека, и укомплектованность объекта в разделе рядом.
      void client.invalidateQueries({ queryKey: peopleKeys.all });
      void client.invalidateQueries({ queryKey: ['org'] });
    },
  });
}

export function useCreateAssignmentsBulkMutation(
  idempotencyKey: string,
): UseMutationResult<BulkAssignmentOut, ApiError, BulkAssignmentIn> {
  const client = useQueryClient();
  return useMutation<BulkAssignmentOut, ApiError, BulkAssignmentIn>({
    mutationFn: (body) => createAssignmentsBulk(body, idempotencyKey),
    // Подтверждения НЕТ намеренно: панель укомплектования НЕ закрывается после действия и
    // сама показывает итог — кого поставили, кого не удалось и почему. Ради этого она и не
    // закрывается; всплывающее «Люди поставлены на объект» поверх такого итога отвечает
    // короче и мимо.
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: peopleKeys.all });
      void client.invalidateQueries({ queryKey: ['org'] });
    },
  });
}

export interface CloseAssignmentVariables {
  assignmentId: string;
  dateTo: string;
}

export function useCloseAssignmentMutation(): UseMutationResult<
  AssignmentOut,
  ApiError,
  CloseAssignmentVariables
> {
  const client = useQueryClient();
  return useMutation<AssignmentOut, ApiError, CloseAssignmentVariables>({
    mutationFn: ({ assignmentId, dateTo }) => closeAssignment(assignmentId, dateTo),
    meta: подтверждение('Человек снят с объекта'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: peopleKeys.all });
      void client.invalidateQueries({ queryKey: ['org'] });
    },
  });
}

export interface ExtendAssignmentVariables {
  assignmentId: string;
  body: ExtendIn;
  /**
   * Последний день, который человек видел в карточке, когда нажал. По нему видно, что ничего
   * не изменилось. `undefined` — карточка не открыта, и сравнить не с чем.
   */
  было?: string | null;
}

/**
 * Продление назначения: новый последний день либо «без даты окончания».
 *
 * Сбрасывает больше, чем закрытие, и это не перестраховка. Продлённый возвращается в табель
 * (сетка строится из назначений), уходит из списка «Не попали в табель» и из дела «На вахте, но
 * не на посту». Ключи табеля и дел пишутся здесь буквально, как и ключ справочников (`org`):
 * разделы друг к другу за кодом не ходят, а корень ключа — это договорённость, а не код.
 *
 * Подтверждение «Назначение продлено» говорит и про ведомость: табель продление подхватывает
 * сразу, а уже созданная ведомость — нет. Оно молчит, когда продлевать было нечего (сервер
 * принял повтор того же дня молча) и когда пришло замечание: тогда панель остаётся открытой и
 * говорит «продлено» сама, рядом с ним, — второе сообщение о том же поверх было бы шумом.
 */
export function useExtendAssignmentMutation(): UseMutationResult<
  AssignmentOut,
  ApiError,
  ExtendAssignmentVariables
> {
  const client = useQueryClient();
  return useMutation<AssignmentOut, ApiError, ExtendAssignmentVariables>({
    mutationFn: ({ assignmentId, body }) => extendAssignment(assignmentId, body),
    meta: подтверждение<ExtendAssignmentVariables, AssignmentOut>((ввод, ответ) => {
      if ((ответ.warnings ?? []).length > 0) {
        return null;
      }
      if (ввод.было !== undefined && ответ.date_to === ввод.было) {
        return null;
      }
      return 'Назначение продлено';
    }, вСозданнуюВедомость('человек')),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: peopleKeys.all });
      void client.invalidateQueries({ queryKey: ['org'] });
      void client.invalidateQueries({ queryKey: ['timesheet'] });
      void client.invalidateQueries({ queryKey: ['cases'] });
    },
  });
}

/**
 * Назначение и сам человек из карточки, открытой под панелью.
 *
 * Панель продления получает из адреса только опознаватель назначения, а сказать человеку, ЧТО
 * он продлевает, с какого дня тот вернётся в табель и можно ли продлевать вовсе (уволен, объект
 * закрыт), без карточки нельзя. Человека берём из адреса карточки (`/people/<вид>/r/<человек>`),
 * а назначения и карточку — теми же запросами и теми же ключами, что у самой карточки: она их
 * уже загрузила, второго запроса нет, а после продления обновятся оба места сразу. Панель
 * открыли не из карточки — оба `undefined`, и она говорит общими словами, а решает сервер.
 */
export function useAssignmentInOpenCard(assignmentId: string): {
  назначение: AssignmentOut | undefined;
  сотрудник: EmployeeOut | undefined;
} {
  const location = useLocation();
  const путь = parsePath(location.pathname);
  const человек = путь !== null && путь.section === SECTION ? путь.rowId : undefined;
  const назначения = useQuery<ListResponse<AssignmentOut>, ApiError>({
    queryKey: peopleKeys.assignments(человек ?? 'нет'),
    queryFn: ({ signal }) => listEmployeeAssignments(человек ?? '', signal),
    enabled: человек !== undefined,
  });
  const карточка = useQuery<EmployeeOut, ApiError>({
    queryKey: peopleKeys.employee(человек ?? 'нет'),
    queryFn: ({ signal }) => getEmployee(человек ?? '', signal),
    enabled: человек !== undefined,
  });
  return {
    назначение: назначения.data?.items.find((назначение) => назначение.id === assignmentId),
    сотрудник: карточка.data,
  };
}

export function useTransferMutation(
  assignmentId: string,
  idempotencyKey: string,
): UseMutationResult<AssignmentOut, ApiError, TransferIn> {
  const client = useQueryClient();
  return useMutation<AssignmentOut, ApiError, TransferIn>({
    mutationFn: (body) => transferAssignment(assignmentId, body, idempotencyKey),
    meta: подтверждение('Человек переведён'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: peopleKeys.all });
      void client.invalidateQueries({ queryKey: ['org'] });
    },
  });
}

export function useRateChangeMutation(
  assignmentId: string,
  idempotencyKey: string,
): UseMutationResult<RateChangeOut, ApiError, RateChangeIn> {
  const client = useQueryClient();
  return useMutation<RateChangeOut, ApiError, RateChangeIn>({
    mutationFn: (body) => changeRate(assignmentId, body, idempotencyKey),
    meta: подтверждение('Ставка изменена'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: peopleKeys.all });
    },
  });
}

// ── Реквизиты выплаты (сценарий 75) ────────────────────────────────────────────────────────
//
// Заявление не правится, а заменяется: новое гасит прежнее, прежнее остаётся историей.
// Поэтому мутация здесь одна — создание, и после неё сбрасывается весь список человека.

export function usePayoutApplicationsQuery(
  employeeId: string,
): UseQueryResult<ListResponse<ApplicationOut>, ApiError> {
  return useQuery<ListResponse<ApplicationOut>, ApiError>({
    queryKey: peopleKeys.payouts(employeeId),
    queryFn: () => fetchApplications({ employee_id: employeeId }),
  });
}

/**
 * Отметки о выплатах этому человеку (тз, раздел 5.4.6: «признак виден в реестре ведомостей и в
 * карточке сотрудника»).
 *
 * До 26.08.2026 признак был виден только в ведомости — то есть на вопрос «а перевели ли ему за
 * июль» приходилось открывать июльскую ведомость и искать в ней строку.
 */
export function usePayoutFactsQuery(
  employeeId: string,
): UseQueryResult<ListResponse<FactOut>, ApiError> {
  return useQuery<ListResponse<FactOut>, ApiError>({
    queryKey: peopleKeys.payoutFacts(employeeId),
    queryFn: () => fetchFacts({ employee_id: employeeId }),
  });
}

export function useCreatePayoutApplicationMutation(
  employeeId: string,
): UseMutationResult<ApplicationOut, ApiError, ApplicationIn> {
  const client = useQueryClient();
  return useMutation<ApplicationOut, ApiError, ApplicationIn>({
    mutationFn: (body) => createApplication(body),
    meta: подтверждение('Заявление о реквизитах сохранено'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: peopleKeys.payouts(employeeId) });
    },
  });
}

// ── Расчётный лист (сценарии 64, 82) ───────────────────────────────────────────────────────

export function usePayslipQuery(
  employeeId: string,
  year: number,
  month: number,
): UseQueryResult<PayslipOut, ApiError> {
  return useQuery<PayslipOut, ApiError>({
    queryKey: peopleKeys.payslip(employeeId, year, month),
    queryFn: () => fetchPayslip(employeeId, { year, month }),
    // Месяца без расчёта — обычное дело, а не сбой: повторять запрос за ним незачем.
    retry: false,
  });
}

// ── Финальный расчёт: что мешает рассчитать человека окончательно ──────────────────────────

export function useFinalSettlement(employeeId: string): UseQueryResult<FinalSettlementOut> {
  return useQuery<FinalSettlementOut>({
    queryKey: ['people', 'final-settlement', employeeId],
    queryFn: () => fetchFinalSettlement(employeeId),
  });
}

/**
 * Решение по долгу. После него блокировка обязана пересчитаться сразу: человек принял решение
 * и должен увидеть, что осталось, а не гадать, засчиталось ли.
 */
export function useDecideDebt(employeeId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      debtId,
      decision,
      basis,
    }: {
      debtId: string;
      decision: DebtDecisionKind;
      basis: string;
    }) => decideDebt(debtId, decision, basis),
    meta: подтверждение('Решение по долгу записано'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['people', 'final-settlement', employeeId] });
      // Решение меняет и сам долг: списанный уходит из действующих, переданный на взыскание
      // меняет состояние. Без сброса человек видит на соседней вкладке прежнюю картину и
      // думает, что решение не сохранилось.
      void client.invalidateQueries({ queryKey: ['people', 'debts', employeeId] });
      void client.invalidateQueries({ queryKey: ['people', 'ticket', employeeId] });
    },
  });
}

/**
 * Списание долга (сценарий 45). Исключительное право руководства и администратора.
 *
 * Сбрасывает то же, что и решение при увольнении, плюс сводку невозвратных потерь: списанный
 * долг обязан появиться там в тот же миг, иначе деньги исчезают из учёта без следа.
 */
export function useWriteOffDebt(employeeId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ debtId, basis }: { debtId: string; basis: string }) =>
      writeOffDebt(debtId, basis),
    meta: подтверждение('Долг списан: сумма ушла в невозвратные потери'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['people', 'debts', employeeId] });
      void client.invalidateQueries({ queryKey: ['people', 'final-settlement', employeeId] });
      // Сводка невозвратных потерь живёт под ключом раздела «Расчёт»: списанный долг обязан
      // появиться в ней сразу, иначе деньги исчезают из учёта без следа.
      void client.invalidateQueries({ queryKey: ['payroll'] });
    },
  });
}

// ── Долги и форма ──────────────────────────────────────────────────────────────────────────

/**
 * Заведение долга вручную (сценарий 41).
 *
 * Сбрасывает долги человека и его расчёт: новый долг встаёт в очередь удержаний и меняет то,
 * что человек получит на руки в ближайшей ведомости.
 */
export function useCreateDebt(employeeId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: DebtIn) => createDebt(body),
    meta: подтверждение('Долг заведён'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['people', 'debts', employeeId] });
      void client.invalidateQueries({ queryKey: ['people', 'final-settlement', employeeId] });
      void client.invalidateQueries({ queryKey: ['payroll'] });
    },
  });
}

export function useUniformItems() {
  return useQuery({
    queryKey: ['uniform', 'items'],
    queryFn: () => fetchUniformItems(),
    // Справочник цен меняется раз в год: держим его в кэше пять минут, иначе каждое открытие
    // вкладки стучится в сервер за одним и тем же прайсом.
    staleTime: 5 * 60 * 1000,
  });
}

export function useUniformIssues(employeeId: string) {
  return useQuery({
    queryKey: ['uniform', 'issues', employeeId],
    queryFn: () => fetchUniformIssues(employeeId),
  });
}

/** Выдача формы. После неё обновляются и выдачи, и долги: долг заводится тем же действием. */
export function useIssueUniform(employeeId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof issueUniform>[0]) =>
      // Ключ рождается здесь, на попытке: повтор той же попытки вернёт прежнюю выдачу.
      issueUniform(body, crypto.randomUUID()),
    meta: подтверждение('Форма выдана: долг заведён тем же действием'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['uniform', 'issues', employeeId] });
      void client.invalidateQueries({ queryKey: ['people', 'debts', employeeId] });
      void client.invalidateQueries({ queryKey: ['people', 'final-settlement', employeeId] });
    },
  });
}

export function useTicketState(employeeId: string) {
  return useQuery({
    queryKey: ['people', 'ticket', employeeId],
    queryFn: () => fetchTicketState(employeeId),
  });
}

/** Отметка о досрочном отъезде. Меняет судьбу билета, поэтому обновляет и долги. */
export function useRegisterEarlyLeave(employeeId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { left_at: string; reason: string; reason_confirmed: boolean }) =>
      registerEarlyLeave(employeeId, body),
    meta: подтверждение('Досрочный отъезд отмечен'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['people', 'ticket', employeeId] });
      void client.invalidateQueries({ queryKey: ['people', 'debts', employeeId] });
    },
  });
}
